import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Logo } from "../zen/Logo";
import { Button } from "../zen/Button";
import { Container } from "../zen/Container";

const NAV_LINKS = [
    { label: "Home", to: "/" },
    { label: "Divisions", to: "/divisions" },
    { label: "BIM", to: "/bim" },
    { label: "Building Materials", to: "/building-materials" },
    { label: "Real Estate", to: "/real-estate" },
    { label: "Consulting", to: "/consulting" },
];

export const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();
    // Quiet mode for focused application surfaces — hides the secondary CTA
    const quiet = location.pathname.startsWith("/bim/inquiry");

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    return (
        <>
            <header
                data-testid="main-navbar"
                className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ease-luxury ${
                    scrolled
                        ? "border-b border-border-subtle bg-[rgba(10,10,11,0.7)] backdrop-blur-[20px]"
                        : "border-b border-transparent bg-transparent"
                }`}
            >
                <Container>
                    <div className="flex h-[68px] items-center justify-between gap-6">
                        <Logo />

                        <nav
                            className="hidden lg:flex items-center gap-8"
                            aria-label="Primary"
                        >
                            {NAV_LINKS.map((link) => (
                                <NavLink
                                    key={link.to}
                                    to={link.to}
                                    end={link.to === "/"}
                                    data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                                    className={({ isActive }) =>
                                        `zen-navlink text-[13.5px] font-medium ${
                                            isActive
                                                ? "text-text-primary"
                                                : "text-text-secondary hover:text-text-primary"
                                        }`
                                    }
                                >
                                    {({ isActive }) => (
                                        <span
                                            data-active={isActive ? "true" : "false"}
                                        >
                                            {link.label}
                                        </span>
                                    )}
                                </NavLink>
                            ))}
                        </nav>

                        <div className="flex items-center gap-3">
                            {!quiet && (
                                <Button
                                    to="/contact"
                                    variant="subtle"
                                    size="sm"
                                    className="hidden lg:inline-flex"
                                    data-testid="nav-contact-btn"
                                >
                                    Contact Us
                                </Button>
                            )}
                            <button
                                className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-full border border-border-subtle text-text-primary hover:border-border-default transition-colors"
                                onClick={() => setOpen(true)}
                                aria-label="Open menu"
                                data-testid="mobile-menu-open-btn"
                            >
                                <Menu className="h-5 w-5" strokeWidth={1.5} />
                            </button>
                        </div>
                    </div>
                </Container>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        key="mobile-sheet"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="fixed inset-0 z-[60] lg:hidden"
                        data-testid="mobile-menu-sheet"
                    >
                        <div
                            className="absolute inset-0"
                            style={{
                                background: "rgba(10,10,11,0.85)",
                                backdropFilter: "blur(24px)",
                                WebkitBackdropFilter: "blur(24px)",
                            }}
                        />
                        <div className="relative h-full flex flex-col">
                            <Container>
                                <div className="flex h-[68px] items-center justify-between">
                                    <Logo />
                                    <button
                                        className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border-subtle text-text-primary"
                                        onClick={() => setOpen(false)}
                                        aria-label="Close menu"
                                        data-testid="mobile-menu-close-btn"
                                    >
                                        <X className="h-5 w-5" strokeWidth={1.5} />
                                    </button>
                                </div>
                            </Container>

                            <nav
                                className="mt-8 flex-1 px-8"
                                aria-label="Mobile primary"
                            >
                                <ul className="flex flex-col gap-1">
                                    {NAV_LINKS.map((link, i) => (
                                        <motion.li
                                            key={link.to}
                                            initial={{ opacity: 0, y: 12 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{
                                                delay: 0.08 + i * 0.05,
                                                duration: 0.4,
                                                ease: [0.16, 1, 0.3, 1],
                                            }}
                                        >
                                            <NavLink
                                                to={link.to}
                                                end={link.to === "/"}
                                                data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                                                className={({ isActive }) =>
                                                    `block py-4 font-display text-[28px] font-semibold tracking-tight-2 ${
                                                        isActive
                                                            ? "text-text-primary"
                                                            : "text-text-secondary"
                                                    }`
                                                }
                                            >
                                                {link.label}
                                            </NavLink>
                                        </motion.li>
                                    ))}
                                </ul>
                            </nav>

                            <div className="px-8 pb-12">
                                <Button
                                    to="/contact"
                                    variant="primary"
                                    size="lg"
                                    className="w-full"
                                    data-testid="mobile-contact-btn"
                                >
                                    Contact Us
                                </Button>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default Navbar;
